<script setup lang="ts">
import Simplebar from "simplebar-vue";
import { computed, ref, shallowRef } from "vue";
import { useRouter } from "vue-router";

import ColorBadge from "@/components/ui/ColorBadge.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import {
  extractIdFromUrn,
  ReportUrnPrefix,
  type CrossValidationReportDto,
  type EstimatorReportDto,
} from "@/dto";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useToastsStore } from "@/stores/toasts";
import CrossValidationReport from "@/views/reports/CrossValidationReport.vue";
import EstimatorReport from "@/views/reports/EstimatorReport.vue";

const { workspaceName, projectName } = defineProps<{
  workspaceName: string;
  projectName: string;
}>();
defineExpose({ openEstimatorReport, openCrossValidationReport, close });

const api = useApi();
const router = useRouter();
const toasts = useToastsStore();

const isOpen = ref(false);
const isLoading = ref(false);
const estimatorReport = shallowRef<EstimatorReportDto>();
const crossValidationReport = shallowRef<CrossValidationReportDto>();
let crossValidationReportEstimators = new Map<number, EstimatorReportDto>();

async function openEstimatorReport(reportId: number) {
  isOpen.value = true;
  isLoading.value = true;
  const r = await api.fetchEstimatorReport(workspaceName, projectName, reportId);
  if (r === null) {
    toasts.addToast("Report not found", "error", { duration: 5 });
  } else {
    estimatorReport.value = r;
    isLoading.value = false;
  }
}

async function openCrossValidationReport(reportId: number) {
  isOpen.value = true;
  isLoading.value = true;
  const r = await api.fetchCrossValidationReport(workspaceName, projectName, reportId);
  if (r === null) {
    toasts.addToast("Report not found", "error", { duration: 5 });
  } else {
    // fetch sub reports and filter potentially not found
    const estimatorReports = (
      await Promise.all(
        r.estimators.map((id) => api.fetchEstimatorReport(workspaceName, projectName, id))
      )
    ).filter((er) => er !== null);
    crossValidationReportEstimators = new Map(estimatorReports.map((e) => [e.id, e]));
    crossValidationReport.value = r;
    isLoading.value = false;
  }
}

function close() {
  isOpen.value = false;
  isLoading.value = false;
  estimatorReport.value = undefined;
  crossValidationReport.value = undefined;
  crossValidationReportEstimators.clear();
}

function openRelatedEstimatorReport(reportId: number) {
  const route = router.resolve({
    name: RouteNames.EstimatorReportDetail,
    params: { workspaceName, projectName, reportId },
  });
  window.open(route.href, "_blank");
}

function openRelatedCrossValidationReport(urn: string) {
  const reportId = extractIdFromUrn(urn);
  const route = router.resolve({
    name: RouteNames.CrossValidationReportDetail,
    params: { workspaceName, projectName, reportId },
  });
  window.open(route.href, "_blank");
}

const parentCrossValidationReportUrn = computed(() => {
  if (estimatorReport.value && "parents_urns" in estimatorReport.value) {
    return (estimatorReport.value as EstimatorReportDto).parents_urns.find((urn) =>
      urn.startsWith(ReportUrnPrefix.CrossValidation)
    );
  }
  return undefined;
});
</script>

<template>
  <Teleport to="body">
    <Transition name="drawer-appear">
      <div v-if="isOpen" class="report-drawer">
        <div class="outside-click-catcher" @click="close" />

        <div class="report-drawer-content">
          <header>
            <div class="title">
              <slot name="title"></slot>
            </div>
            <div class="close">
              <SimpleButton icon-left="icon-close" kind="ghost" @click="close" />
            </div>
          </header>

          <Simplebar class="scroller">
            <SkoreSpinner v-if="isLoading" />

            <template v-else-if="estimatorReport !== undefined">
              <ColorBadge
                v-if="parentCrossValidationReportUrn"
                class="parent-info"
                color="blue"
                size="l"
              >
                <i class="icon icon-info-circle-2" />
                This estimator is related to
                <i class="icon icon-playground" />
                <span class="link" @click="close">
                  {{ parentCrossValidationReportUrn }}
                </span>
              </ColorBadge>

              <EstimatorReport
                :report="estimatorReport"
                @open-cross-validation-report="openRelatedCrossValidationReport"
              />
            </template>

            <template v-else-if="crossValidationReport !== undefined">
              <CrossValidationReport
                :report="crossValidationReport"
                :estimators="crossValidationReportEstimators"
                @open-estimator="openRelatedEstimatorReport"
              />
            </template>
          </Simplebar>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.report-drawer {
  position: fixed;
  z-index: 8000;
  top: 0;
  left: 0;
  display: flex;
  width: 100dvw;
  height: 100dvh;
  justify-content: flex-end;
  background-color: rgb(from var(--color-gray-900) r g b / 30%);
  transition: background-color var(--animation-duration) var(--animation-easing);

  .outside-click-catcher {
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
  }

  .report-drawer-content {
    z-index: 2;
    display: flex;
    overflow: hidden;
    width: 88dvw;
    height: calc(100dvh - (var(--numbers-16) * 2));
    min-height: 0;
    flex-direction: column;
    padding: var(--numbers-32) var(--numbers-32) 0 var(--numbers-32);
    border-radius: var(--numbers-16);
    margin: var(--numbers-16) var(--numbers-16) var(--numbers-16) 0;
    background-color: var(--background-primary);
    gap: var(--numbers-16);
    transition: transform var(--animation-duration) var(--animation-easing);

    header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      :deep(.skore-button) {
        --height: var(--numbers-12);

        border: none;
        font-size: var(--numbers-10);
      }
    }

    .parent-info {
      width: calc(100% - (var(--numbers-12)));
      margin-bottom: var(--numbers-16);
      text-align: left;

      .icon {
        vertical-align: middle;
      }

      .link {
        cursor: pointer;
        text-decoration: underline;
      }
    }

    .scroller {
      min-height: 0;
      flex: 1 1 auto;

      /* Keep Simplebar sized to the available space */
      :deep(.simplebar-wrapper),
      :deep(.simplebar-content-wrapper) {
        height: 100%;
      }
    }
  }
}

.drawer-appear-enter-from,
.drawer-appear-leave-to {
  background-color: transparent;

  .report-drawer-content {
    transform: translateX(100%);
  }
}
</style>
